import { useRef, useState } from 'react';

import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';

import AutocompleteCreators from '@/components/autocompleteCreators/AutocompleteCreators';
import { inputSx } from '@/constants/sx';
import { useFetchCreatorsAutocomplete } from '@/hooks/queries/creators';
import { useAppSelector } from '@/hooks/redux';
import { Evaluation } from '@/models/evaluations';

interface Step1Props {
  errors: Evaluation.FormErrors;
  values: Evaluation.Form;
  onChange: (field: keyof Evaluation.Form, value: unknown) => void;
  onClickContinue: () => void;
  isDisabled?: boolean;
  initialDateMode?: boolean;
}

const MEDIA_TYPE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'image', label: 'Image' },
  { value: 'video', label: 'Video' }
] as const;

const PLATFORM_TREE = [
  {
    value: 'youtube',
    label: 'Youtube',
    children: [
      { value: 'videos', label: 'Videos' },
      { value: 'shorts', label: 'Shorts' }
    ]
  },
  {
    value: 'instagram',
    label: 'Instagram',
    children: [
      { value: 'posts', label: 'Posts' },
      { value: 'reels', label: 'Reels' },
      { value: 'stories', label: 'Stories' }
    ]
  },
  {
    value: 'tiktok',
    label: 'Tik Tok',
    children: [
      { value: 'posts', label: 'Posts' },
      { value: 'stories', label: 'Stories' }
    ]
  }
] as const;

type PlatformChildSelection = Record<string, boolean>;
type PlatformSelection = Record<string, boolean | PlatformChildSelection>;

const getPlatformState = (
  platforms: PlatformSelection,
  platform: (typeof PLATFORM_TREE)[number]
) => {
  const childValues = platform.children.map((child) => child.value);
  const selection = (platforms[platform.value] as PlatformChildSelection) || {};
  const checkedCount = childValues.filter((value) => selection[value]).length;

  return {
    selection,
    isChecked: childValues.length
      ? checkedCount === childValues.length
      : Boolean(platforms[platform.value]),
    isIndeterminate: checkedCount > 0 && checkedCount < childValues.length
  };
};

const togglePlatform = (
  platforms: PlatformSelection,
  platform: (typeof PLATFORM_TREE)[number]
): PlatformSelection => {
  const childValues = platform.children.map((child) => child.value);
  const { isChecked } = getPlatformState(platforms, platform);

  return {
    ...platforms,
    [platform.value]: childValues.length
      ? Object.fromEntries(
          childValues.map((value): [string, boolean] => [value, !isChecked])
        )
      : !isChecked
  };
};

const toggleChild = (
  platforms: PlatformSelection,
  parentValue: string,
  childValue: string
): PlatformSelection => {
  const selection = (platforms[parentValue] as PlatformChildSelection) || {};
  return {
    ...platforms,
    [parentValue]: { ...selection, [childValue]: !selection[childValue] }
  };
};

const Step1 = ({
  errors,
  values,
  onChange,
  onClickContinue,
  isDisabled = false,
  initialDateMode = false
}: Step1Props) => {
  const [isOngoing, setIsOngoing] = useState<boolean>(initialDateMode);
  const lastDateRangeRef = useRef<Evaluation.DateRange | null>(null);

  const user = useAppSelector((state) => state.user.currentUser);
  const generalTerm = user?.workspaceStorageTerms?.general;

  const {
    title,
    platforms,
    choosenCreators,
    followersRange,
    dates,
    mediaType
  } = values;
  const { data: availableCreators } = useFetchCreatorsAutocomplete({
    params: { groups: true, creators: true }
  });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') onClickContinue();
  };

  const handleChangeFollowers = ({
    target: { value, name }
  }: React.ChangeEvent<HTMLInputElement>) => {
    onChange('followersRange', { ...followersRange, [name]: value });
  };

  const handleToggleOngoing = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;

    if (checked) {
      if (dates[0] && dates[1]) lastDateRangeRef.current = dates;
      onChange('dates', [dates[0] || new Date(), null]);
    } else {
      onChange(
        'dates',
        lastDateRangeRef.current ?? [
          dates[0] || new Date(),
          dates[0] || new Date()
        ]
      );
    }
    setIsOngoing(checked);
  };

  return (
    <Stack direction="row" gap={3} mt={1}>
      <Stack gap={3} flex={1}>
        <FormControl variant="outlined">
          <TextField
            id="evaluation-title"
            error={Boolean(errors.title)}
            helperText={errors.title}
            label="Evaluation title"
            onChange={({ target: { value } }) => onChange('title', value)}
            onKeyDown={handleKeyDown}
            value={title}
            size="small"
            sx={inputSx}
          />
        </FormControl>
        <Stack>
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            id="evaluation-date-range"
          >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <DatePicker
                  disableFuture
                  label="Start date"
                  value={dates[0]}
                  minDate={new Date(generalTerm)}
                  onChange={(newValue) => {
                    const [, end] = dates;
                    const startIsAfterEnd =
                      newValue && end && newValue.getTime() > end.getTime();

                    onChange('dates', [
                      newValue,
                      startIsAfterEnd ? newValue : end
                    ]);
                  }}
                  format="dd/MM/yyyy"
                  disabled={isDisabled}
                  slotProps={{
                    textField: {
                      size: 'small',
                      variant: 'outlined',
                      fullWidth: true,
                      sx: inputSx
                    }
                  }}
                />
              </Box>
              <Box
                style={{ visibility: isOngoing ? 'hidden' : 'visible' }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
                aria-hidden={isOngoing}
              >
                <Typography color="textSecondary">-</Typography>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <DatePicker
                    label="End date"
                    value={dates[1]}
                    minDate={dates[0] || new Date(generalTerm)}
                    onChange={(newValue) =>
                      onChange('dates', [dates[0], newValue])
                    }
                    format="dd/MM/yyyy"
                    disabled={isDisabled || isOngoing}
                    slotProps={{
                      textField: {
                        size: 'small',
                        variant: 'outlined',
                        fullWidth: true,
                        sx: inputSx
                      }
                    }}
                  />
                </Box>
              </Box>
            </LocalizationProvider>
          </Stack>
          <FormControlLabel
            control={
              <Checkbox
                checked={isOngoing}
                disabled={isDisabled}
                onChange={handleToggleOngoing}
                disableRipple
              />
            }
            label="On going"
            id="evaluation-ongoing"
          />
        </Stack>
      </Stack>
      <Stack gap={3} flex={1} sx={inputSx}>
        <Autocomplete
          fullWidth
          disableClearable
          size="small"
          options={MEDIA_TYPE_OPTIONS}
          value={MEDIA_TYPE_OPTIONS.find((opt) => opt.value === mediaType)}
          onChange={(_, option) =>
            option && onChange('mediaType', option.value)
          }
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          disabled={isDisabled}
          renderInput={(params) => <TextField label="Media type" {...params} />}
          id="evaluation-mediatype"
        />
        <Stack id="evaluation-creators">
          <AutocompleteCreators
            disabled={isDisabled}
            value={choosenCreators}
            onChange={(
              _event: React.SyntheticEvent,
              value: Evaluation.Creator[]
            ) => onChange('choosenCreators', value)}
            creatorGroups={availableCreators?.creatorGroups}
            creators={availableCreators?.creators}
            label="Creators"
            size="small"
            helperText="If none is selected, the system will search all creators."
          />
        </Stack>
        <Stack id="evaluation-followers-range">
          <Stack direction="row" alignItems="center" gap={2}>
            <TextField
              fullWidth
              label="Followers Range Min"
              name="min"
              type="number"
              size="small"
              inputProps={{ min: 0, max: followersRange.max }}
              onChange={handleChangeFollowers}
              value={followersRange.min}
              disabled={isDisabled}
              error={Boolean(errors.followersRange)}
            />
            <Typography>-</Typography>
            <TextField
              fullWidth
              label="Followers Range Max"
              name="max"
              type="number"
              size="small"
              inputProps={{ min: followersRange.min }}
              onChange={handleChangeFollowers}
              value={followersRange.max}
              disabled={isDisabled}
            />
          </Stack>
          {errors.followersRange && (
            <FormHelperText style={{ margin: 0, padding: 0 }} error>
              {errors.followersRange}
            </FormHelperText>
          )}
        </Stack>
        <Stack id="evaluation-platform" gap={1}>
          <Stack direction="row" flexWrap="wrap" gap={4}>
            {PLATFORM_TREE.map((platform) => {
              const { selection, isChecked, isIndeterminate } =
                getPlatformState(platforms, platform);

              return (
                <Stack key={platform.value}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isChecked}
                        indeterminate={isIndeterminate}
                        disabled={isDisabled}
                        onChange={() =>
                          onChange(
                            'platforms',
                            togglePlatform(platforms, platform)
                          )
                        }
                      />
                    }
                    label={platform.label}
                  />
                  {platform.children.map((child) => (
                    <FormControlLabel
                      key={child.value}
                      sx={{ ml: 1 }}
                      control={
                        <Checkbox
                          checked={Boolean(selection[child.value])}
                          disabled={isDisabled}
                          onChange={() =>
                            onChange(
                              'platforms',
                              toggleChild(
                                platforms,
                                platform.value,
                                child.value
                              )
                            )
                          }
                        />
                      }
                      label={child.label}
                    />
                  ))}
                </Stack>
              );
            })}
          </Stack>
          {errors.platforms && (
            <FormHelperText error>{errors.platforms}</FormHelperText>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default Step1;
